import { useEffect, useMemo, useState } from "react";
import { getRentalBookings, refundRentalBookingAmount, updateRentalBookingStatus } from "../../../services/rentalService";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableEmptyRow,
  TableActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import { paginateClientList } from "../../../utils/clientPagination";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import { bookingLateReturnTotal, computeRentalLateReturnCharge } from "../../../utils/rentalLateReturn";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardHeader,
} from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { getTableEmptyProps } from "../../common/supplierTableEmpty";

const toDatetimeLocal = (v) => {
  const d = v ? new Date(v) : new Date();
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const isVehicleBooking = (booking) =>
  String(booking?.item?.category?.form_type || "").toLowerCase() === "vehicle";

const badge = (status) => {
  const s = String(status || "").toLowerCase();
  const base = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold";
  if (s === "confirmed") return `${base} bg-emerald-50 text-emerald-700 border border-emerald-100`;
  if (s === "on_rent") return `${base} bg-blue-50 text-blue-700 border border-blue-100`;
  if (s === "completed") return `${base} bg-gray-50 text-gray-700 border border-gray-200`;
  if (s === "cancelled") return `${base} bg-red-50 text-red-700 border border-red-100`;
  return `${base} bg-yellow-50 text-yellow-800 border border-yellow-100`;
};

const completedPaid = (booking) =>
  (booking?.rental_payments || booking?.rentalPayments || [])
    .filter((p) => String(p?.status || "").toLowerCase() === "completed")
    .reduce((s, p) => s + (Number(p?.amount || 0) || 0), 0);

/** Completed payments that are NOT cash rows (so we can avoid double-counting cash_received_amount). */
const completedNonCashPaid = (booking) =>
  (booking?.rental_payments || booking?.rentalPayments || [])
    .filter((p) => String(p?.status || "").toLowerCase() === "completed")
    .filter((p) => !String(p?.order_id || "").toLowerCase().startsWith("cash_"))
    .filter((p) => String(p?.payment_method || "").toLowerCase() !== "cash")
    .reduce((s, p) => s + (Number(p?.amount || 0) || 0), 0);

/** Completed cash rows stored as payments (order_id cash_* or payment_method cash). */
const completedCashPaid = (booking) =>
  (booking?.rental_payments || booking?.rentalPayments || [])
    .filter((p) => String(p?.status || "").toLowerCase() === "completed")
    .filter(
      (p) =>
        String(p?.order_id || "").toLowerCase().startsWith("cash_") ||
        String(p?.payment_method || "").toLowerCase() === "cash"
    )
    .reduce((s, p) => s + (Number(p?.amount || 0) || 0), 0);

/** Razorpay amounts that can be refunded online (completed charge with payment_id; excludes cash_ rows). */
const completedRazorpayPaid = (booking) =>
  (booking?.rental_payments || booking?.rentalPayments || [])
    .filter((p) => String(p?.status || "").toLowerCase() === "completed")
    .filter((p) => String(p?.payment_method || "").toLowerCase() === "razorpay")
    .filter((p) => String(p?.payment_id || "").trim() !== "")
    .filter((p) => !String(p?.order_id || "").toLowerCase().startsWith("cash_"))
    .reduce((s, p) => s + (Number(p?.amount || 0) || 0), 0);

const bookingDeposit = (booking) => {
  const d = Number(booking?.deposit_amount || 0) || 0;
  if (d > 0) return d;
  return Number(booking?.item?.pricing_rule?.security_deposit || booking?.item?.pricingRule?.security_deposit || 0) || 0;
};

/** Amount still refundable (deposit on booking / vehicle minus cumulative refunds already recorded). */
const refundableDepositAmount = (booking) => {
  const dep = bookingDeposit(booking);
  const already = Number(booking?.deposit_refunded_amount ?? 0) || 0;
  return Math.max(0, dep - already);
};

export default function RentalManagement() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [activeEditTab, setActiveEditTab] = useState("on_rent");
  const [handover, setHandover] = useState({
    pickup_km: "",
    pickup_at: "",
    cash_received_amount: "",
    supplier_note: "",
  });
  const [returnForm, setReturnForm] = useState({
    return_km: "",
    returned_at: "",
    cash_received_amount: "",
    refund_deposit: false,
    refund_method: "online",
    deposit_refunded_amount: "",
    refund_reason: "",
    return_supplier_note: "",
  });

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getRentalBookings();
      const nextRows = Array.isArray(res?.data) ? res.data : [];
      setRows(nextRows);
      // If a booking is currently open in the modal, refresh it from latest list so DB changes reflect immediately.
      if (selected?.id) {
        const latest = nextRows.find((b) => Number(b?.id) === Number(selected.id));
        if (latest) setSelected(latest);
      }
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.message || e?.message || "Failed to load rental bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const items = useMemo(() => {
    return rows.map((b) => ({
      id: b.id,
      rentalTitle: b?.item?.title || "-",
      userName: b?.user?.name || b?.user?.email || "-",
      start: formatListingDateTime(b?.start_datetime),
      end: formatListingDateTime(b?.end_datetime),
      status: b?.status || "pending",
      booking: b,
    }));
  }, [rows]);

  const { data: pagedItems, pagination } = useMemo(
    () => paginateClientList(items, currentPage),
    [items, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [rows]);

  const openEdit = (r) => {
    setError("");
    const booking = r?.booking || null;
    setSelected(booking);
    setHandover({
      pickup_km: booking?.pickup_km != null ? String(booking.pickup_km) : "",
      pickup_at: toDatetimeLocal(booking?.on_rent_at),
      cash_received_amount:
        booking?.cash_received_amount != null ? String(booking.cash_received_amount) : "",
      supplier_note: booking?.supplier_note || "",
    });
    const refundable = refundableDepositAmount(booking);
    setReturnForm({
      return_km: booking?.return_km != null ? String(booking.return_km) : "",
      returned_at: toDatetimeLocal(booking?.returned_at),
      cash_received_amount: booking?.cash_received_amount != null ? String(booking.cash_received_amount) : "",
      // Only pre-check "refund now" when there is remaining deposit headroom (not because status is already refunded).
      refund_deposit: refundable > 0,
      refund_method: "online",
      deposit_refunded_amount: refundable > 0 ? String(refundable) : "",
      refund_reason: "",
      return_supplier_note: booking?.return_supplier_note || "",
    });
    const status = String(booking?.status || "").toLowerCase();
    const returnStatus = String(booking?.return_status || "").toLowerCase();
    // Default to pickup tab unless the vehicle is already returned / booking completed.
    if (returnStatus === "returned" || status === "completed") {
      setActiveEditTab("returned");
    } else {
      setActiveEditTab("on_rent");
    }
    setEditOpen(true);
  };

  const selectedOnlinePaid = Number(completedNonCashPaid(selected) || 0);
  const selectedCashFromBooking = Number(selected?.cash_received_amount || 0) || 0;
  const selectedCashFromPayments = Number(completedCashPaid(selected) || 0);
  // Cash might be represented either as cash_received_amount OR as a cash_* payment row. Use the larger to avoid double counting.
  const selectedCashPaid = Math.max(selectedCashFromBooking, selectedCashFromPayments);
  const selectedPaid = selectedOnlinePaid + selectedCashPaid;
  const selectedRazorpayPaid = Number(completedRazorpayPaid(selected) || 0);
  const selectedTotal = Number(selected?.total_price || 0) || 0;
  const selectedSavedLateReturn = bookingLateReturnTotal(selected);
  const selectedPricing =
    selected?.item?.pricing_rule || selected?.item?.pricingRule || {};
  const selectedLateReturnPreview =
    activeEditTab === "returned" && returnForm.returned_at && selected?.end_datetime
      ? computeRentalLateReturnCharge({
          scheduledEnd: selected.end_datetime,
          actualReturn: new Date(returnForm.returned_at).toISOString(),
          pricing: selectedPricing,
        })
      : null;
  const selectedLateReturnTotal = selectedSavedLateReturn || selectedLateReturnPreview?.lateReturnTotal || 0;
  const selectedAmountDue = selectedTotal + selectedLateReturnTotal;
  const selectedBalance = Math.max(0, selectedAmountDue - selectedPaid);
  const selectedDeposit = bookingDeposit(selected);
  const selectedRefunds = (selected?.payment_refunds || selected?.paymentRefunds || [])
    .slice()
    .sort((a, b) => new Date(b?.refunded_at || b?.created_at || 0).getTime() - new Date(a?.refunded_at || a?.created_at || 0).getTime());
  const selectedAlreadyRefunded = Number(selected?.deposit_refunded_amount ?? 0) || 0;
  const selectedMaxOnlineRefundable = Math.max(0, selectedRazorpayPaid - selectedAlreadyRefunded);
  const selectedIsVehicle = isVehicleBooking(selected);

  const saveHandover = async () => {
    if (!selected?.id) return;
    const vehicle = isVehicleBooking(selected);
    const pk = Number(handover.pickup_km);
    if (vehicle && (handover.pickup_km === "" || !Number.isFinite(pk) || pk < 0)) {
      setToast({ type: "error", message: "Please enter a valid pickup odometer (km)." });
      return;
    }
    try {
      const payload = {
        status: "on_rent",
        pickup_at: handover.pickup_at ? new Date(handover.pickup_at).toISOString() : undefined,
        supplier_note: handover.supplier_note || "",
      };
      if (vehicle) {
        payload.pickup_km = Number(handover.pickup_km);
      }
      if (handover.cash_received_amount !== "") {
        const cash = Number(handover.cash_received_amount);
        if (!Number.isFinite(cash) || cash < 0) {
          setToast({ type: "error", message: "Cash received must be a number ≥ 0." });
          return;
        }
        payload.cash_received_amount = cash;
      }

      await updateRentalBookingStatus(selected.id, payload);
      setToast({ type: "success", message: "Pickup details saved." });
      setSelected(null);
      setEditOpen(false);
      await refresh();
    } catch (e) {
      setToast({
        type: "error",
        message: e?.response?.data?.message || e?.message || "Failed to update booking.",
      });
    }
  };

  const saveReturn = async () => {
    if (!selected?.id) return;
    const vehicle = isVehicleBooking(selected);
    const rk = Number(returnForm.return_km);
    if (vehicle && (returnForm.return_km === "" || !Number.isFinite(rk) || rk < 0)) {
      setToast({ type: "error", message: "Please enter a valid return odometer (km)." });
      return;
    }
    if (vehicle) {
      const pickupKm = Number(selected?.pickup_km);
      if (Number.isFinite(pickupKm) && rk < pickupKm) {
        setToast({ type: "error", message: "Return km cannot be less than pickup km." });
        return;
      }
    }
    if (returnForm.refund_deposit && (returnForm.deposit_refunded_amount === "" || Number(returnForm.deposit_refunded_amount) < 0)) {
      setToast({ type: "error", message: "Please enter refund amount." });
      return;
    }
    const refundable = refundableDepositAmount(selected);
    const requestedRefund = Number(returnForm.deposit_refunded_amount || 0) || 0;
    const isOverDeposit = returnForm.refund_deposit && requestedRefund > refundable && requestedRefund > 0;
    const overReasonText =
      String(returnForm.refund_reason || "").trim() || String(returnForm.return_supplier_note || "").trim();
    if (isOverDeposit && !overReasonText) {
      setToast({
        type: "error",
        message:
          "Refund amount is more than refundable deposit. Add a reason in the yellow box or in return notes below.",
      });
      return;
    }
    const returnNoteTrim = String(returnForm.return_supplier_note || "").trim();
    const refundReasonTrim = String(returnForm.refund_reason || "").trim();
    if (returnForm.cash_received_amount !== "") {
      const cash = Number(returnForm.cash_received_amount);
      if (!Number.isFinite(cash) || cash < 0) {
        setToast({ type: "error", message: "Cash received must be a number ≥ 0." });
        return;
      }
    }
    try {
      // Mark returned first so a failed follow-up does not leave "refunded but not returned".
      const returnPayload = {
        return_status: "returned",
        returned_at: returnForm.returned_at ? new Date(returnForm.returned_at).toISOString() : undefined,
        cash_received_amount:
          returnForm.cash_received_amount === "" ? undefined : Number(returnForm.cash_received_amount),
        return_supplier_note: returnNoteTrim,
        refund_deposit: 0,
      };
      if (vehicle) {
        returnPayload.return_km = Number(returnForm.return_km);
      }
      await updateRentalBookingStatus(selected.id, returnPayload);
      if (returnForm.refund_deposit && Number(returnForm.deposit_refunded_amount || 0) > 0) {
        const effectiveMethod =
          String(returnForm.refund_method || "online") === "online" &&
          requestedRefund <= selectedMaxOnlineRefundable
            ? "online"
            : "offline";
        await refundRentalBookingAmount(selected.id, {
          amount: Number(returnForm.deposit_refunded_amount),
          return_note: returnNoteTrim,
          refund_reason: refundReasonTrim,
          refund_method: effectiveMethod,
        });
      }
      setToast({ type: "success", message: "Return details saved." });
      setSelected(null);
      setEditOpen(false);
      await refresh();
    } catch (e) {
      setToast({
        type: "error",
        message: e?.response?.data?.message || e?.message || "Failed to update booking.",
      });
      await refresh();
    }
  };

  const handleSaveEdit = async () => {
    if (activeEditTab === "on_rent") {
      await saveHandover();
      return;
    }
    await saveReturn();
  };

  const pageMeta = SUPPLIER_PAGE_META.rentalManagement;

  return (
    <AdminPageLayout>
      {toast && (
        <Toster
          message={toast.message}
          type={toast.type}
          duration={2500}
          onClose={() => setToast(null)}
        />
      )}
      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      {error ? <div className="mb-4 text-sm text-red-600 font-semibold">{error}</div> : null}

      <AdminPageCard>
        <AdminPageCardHeader
          title="Rental bookings"
          actions={
            <button
              type="button"
              onClick={refresh}
              className="px-3 py-1.5 rounded-md text-sm font-semibold border border-gray-200 hover:bg-gray-50"
            >
              Refresh
            </button>
          }
        />

        {loading ? (
          <div className="p-6 text-gray-500">Loading…</div>
        ) : (
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              !loading && items.length > 0 ? (
                <AdminTableFooter>
                  <Pagination pagination={pagination} onPageChange={setCurrentPage} />
                </AdminTableFooter>
              ) : null
            }
          >
            <AdminTableHead>
              <AdminTableTh>#</AdminTableTh>
              <AdminTableTh>Rental</AdminTableTh>
              <AdminTableTh>User</AdminTableTh>
              <AdminTableTh>Start</AdminTableTh>
              <AdminTableTh>End</AdminTableTh>
              <AdminTableTh>Status</AdminTableTh>
              <AdminTableTh align="right">Actions</AdminTableTh>
            </AdminTableHead>
            <AdminTableBody>
              {items.length === 0 ? (
                <TableEmptyRow
                  colSpan={7}
                  {...getTableEmptyProps("rentalManagement")}
                />
              ) : (
                pagedItems.map((r, i) => (
                  <AdminTableRow key={r.id}>
                    <AdminTableCell className="text-gray-700">
                      {(currentPage - 1) * 10 + i + 1}
                    </AdminTableCell>
                    <AdminTableCell className="font-semibold text-gray-900">
                      {r.rentalTitle}
                    </AdminTableCell>
                    <AdminTableCell className="text-gray-700">{r.userName}</AdminTableCell>
                    <AdminTableCell className="text-gray-700">{r.start}</AdminTableCell>
                    <AdminTableCell className="text-gray-700">{r.end}</AdminTableCell>
                    <AdminTableCell>
                      <span className={badge(r.status)}>
                        {String(r.status).replaceAll("_", " ")}
                      </span>
                    </AdminTableCell>
                    <TableActionsCell>
                      {String(r.status || "").toLowerCase() !== "cancelled" && (
                        <TableActionButton
                          variant="view"
                          label="Manage"
                          onClick={() => openEdit(r)}
                        />
                      )}
                    </TableActionsCell>
                  </AdminTableRow>
                ))
              )}
            </AdminTableBody>
          </AdminTable>
        )}
      </AdminPageCard>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Booking workflow details"
        width="w-[62rem]"
        primaryButtonText={activeEditTab === "on_rent" ? "Confirm on rent" : "Confirm returned"}
        primaryButtonAction={handleSaveEdit}
      >
        <div className="space-y-4 p-2">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-xs text-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              <div><span className="font-semibold">Booking ID:</span> #{selected?.id || "-"}</div>
              <div><span className="font-semibold">Rental:</span> {selected?.item?.title || "-"}</div>
              <div><span className="font-semibold">Customer:</span> {selected?.user?.name || selected?.user?.email || "-"}</div>
              <div><span className="font-semibold">Contact:</span> {selected?.user?.phone || selected?.user?.email || "-"}</div>
              <div><span className="font-semibold">Pickup location:</span> {selected?.pickup_location || "-"}</div>
              <div><span className="font-semibold">Dropoff location:</span> {selected?.dropoff_location || "-"}</div>
              <div><span className="font-semibold">Trip start:</span> {formatListingDateTime(selected?.start_datetime)}</div>
              <div><span className="font-semibold">Trip end:</span> {formatListingDateTime(selected?.end_datetime)}</div>
            </div>
            <div className="mt-3 border-t border-gray-200 pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              <div><span className="font-semibold">Total booking amount:</span> ₹{selectedTotal.toFixed(2)}</div>
              <div><span className="font-semibold">Amount paid:</span> ₹{selectedPaid.toFixed(2)}</div>
              {selectedLateReturnTotal > 0 ? (
                <div>
                  <span className="font-semibold">Late return charge:</span> ₹{selectedLateReturnTotal.toFixed(2)}
                  {selectedLateReturnPreview ? (
                    <span className="ml-1 text-amber-700">
                      ({selectedLateReturnPreview.lateReturnHours} hr × ₹
                      {Number(selectedPricing.price_per_hour || 0).toFixed(2)}/hr, after 1 hr grace)
                    </span>
                  ) : selected?.late_return_hours ? (
                    <span className="ml-1 text-gray-500">
                      ({selected.late_return_hours} hr late, after 1 hr grace)
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div><span className="font-semibold">Pending balance:</span> ₹{selectedBalance.toFixed(2)}</div>
              <div><span className="font-semibold">Deposit:</span> ₹{selectedDeposit.toFixed(2)} ({selected?.deposit_status || "pending"})</div>
            </div>
          </div>

          <div className="flex gap-2 border-b border-gray-200 pb-3">
            <button
              type="button"
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                activeEditTab === "on_rent" ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-700"
              }`}
              onClick={() => setActiveEditTab("on_rent")}
            >
              On rent
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                activeEditTab === "returned" ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-700"
              }`}
              onClick={() => setActiveEditTab("returned")}
            >
              Return
            </button>
          </div>

          {activeEditTab === "on_rent" ? (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Pickup time *</label>
                <input
                  type="datetime-local"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={handover.pickup_at}
                  onChange={(e) => setHandover((p) => ({ ...p, pickup_at: e.target.value }))}
                />
              </div>

              {selectedIsVehicle ? (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Pickup km (current odometer) *</label>
                <input
                  type="number"
                  min="0"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={handover.pickup_km}
                  onChange={(e) => setHandover((p) => ({ ...p, pickup_km: e.target.value }))}
                  placeholder="e.g. 24500"
                />
              </div>
              ) : null}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Cash received (optional)
                </label>
                <input
                  type="number"
                  min="0"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={handover.cash_received_amount}
                  onChange={(e) =>
                    setHandover((p) => ({ ...p, cash_received_amount: e.target.value }))
                  }
                  placeholder="e.g. 1500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={handover.supplier_note}
                  onChange={(e) => setHandover((p) => ({ ...p, supplier_note: e.target.value }))}
                  placeholder="Any handover note (documents checked, damages, etc.)"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Dropoff time *</label>
                <input
                  type="datetime-local"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={returnForm.returned_at}
                  onChange={(e) => setReturnForm((p) => ({ ...p, returned_at: e.target.value }))}
                />
                {selected?.end_datetime ? (
                  <p className="mt-1 text-[11px] text-gray-500">
                    Scheduled drop-off: <span className="font-semibold">{formatListingDateTime(selected.end_datetime)}</span>
                  </p>
                ) : null}
                {selectedLateReturnPreview ? (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <p className="font-semibold">Late return additional charge</p>
                    <p className="mt-1">
                      Return is more than 1 hour past scheduled drop-off. Additional amount:{" "}
                      <span className="font-semibold">₹{selectedLateReturnPreview.lateReturnTotal.toFixed(2)}</span>{" "}
                      ({selectedLateReturnPreview.lateReturnHours} hour
                      {selectedLateReturnPreview.lateReturnHours === 1 ? "" : "s"} × ₹
                      {Number(selectedPricing.price_per_hour || 0).toFixed(2)}/hr, incl. GST &amp; fees).
                    </p>
                  </div>
                ) : returnForm.returned_at && selected?.end_datetime ? (
                  <p className="mt-1 text-[11px] text-emerald-700">
                    No late return charge (on time or within 1 hour grace period).
                  </p>
                ) : null}
              </div>

              {selectedIsVehicle ? (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Return km (odometer) *</label>
                <input
                  type="number"
                  min="0"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={returnForm.return_km}
                  onChange={(e) => setReturnForm((p) => ({ ...p, return_km: e.target.value }))}
                  placeholder="e.g. 24980"
                />
                {selected?.pickup_km != null && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    Pickup km: <span className="font-semibold">{selected.pickup_km}</span>
                  </p>
                )}
              </div>
              ) : (
                <p className="text-xs text-gray-500 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  Marking returned will free one unit back into available stock for this item.
                </p>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Cash received for pending balance</label>
                <input
                  type="number"
                  min="0"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={returnForm.cash_received_amount}
                  onChange={(e) => setReturnForm((p) => ({ ...p, cash_received_amount: e.target.value }))}
                  placeholder="e.g. 1500"
                />
                <label className="block text-xs font-semibold text-gray-600 mt-2 mb-1">
                  Cash / note from pickup <span className="font-normal text-gray-400">(saved when marking on rent)</span>
                </label>
                <div className="w-full min-h-[2.5rem] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {String(selected?.supplier_note || "").trim() ? String(selected.supplier_note).trim() : "—"}
                </div>
              </div>

              <div className="rounded-lg border border-gray-100 p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={returnForm.refund_deposit}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setReturnForm((p) => {
                        const next = { ...p, refund_deposit: checked };
                        if (checked && selected) {
                          const rem = refundableDepositAmount(selected);
                          const cur = Number(p.deposit_refunded_amount);
                          if (p.deposit_refunded_amount === "" || !Number.isFinite(cur) || cur <= 0) {
                            next.deposit_refunded_amount = rem > 0 ? String(rem) : "";
                          }
                        }
                        return next;
                      });
                    }}
                  />
                  Refund deposit now
                </label>
                {returnForm.refund_deposit ? (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Refund method</label>
                    <select
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3"
                      value={returnForm.refund_method}
                      onChange={(e) => setReturnForm((p) => ({ ...p, refund_method: e.target.value }))}
                    >
                      <option value="offline">Offline (manual)</option>
                      <option value="online">Online (Razorpay)</option>
                    </select>

                    <label className="block text-xs font-semibold text-gray-600 mb-1">Refund amount</label>
                    <p className="text-[11px] text-gray-500 mb-1.5">
                      Deposit on booking:{" "}
                      <span className="font-semibold text-gray-700">₹{selectedDeposit.toFixed(2)}</span>
                      {" · "}
                      Already refunded:{" "}
                      <span className="font-semibold text-gray-700">₹{selectedAlreadyRefunded.toFixed(2)}</span>
                      {" · "}
                      Remaining refundable (deposit):{" "}
                      <span className="font-semibold text-gray-700">
                        ₹{refundableDepositAmount(selected).toFixed(2)}
                      </span>
                    </p>
                    <input
                      type="number"
                      min="0"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={returnForm.deposit_refunded_amount}
                      onChange={(e) => {
                        const v = e.target.value;
                        setReturnForm((p) => ({ ...p, deposit_refunded_amount: v }));
                      }}
                    />
                    {(() => {
                      const refundable = selected ? refundableDepositAmount(selected) : 0;
                      const n = Number(returnForm.deposit_refunded_amount || 0) || 0;
                      const over = n > refundable && n > 0;
                      if (!over) return null;
                      return (
                        <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-2">
                          <div className="text-xs font-semibold text-yellow-800">
                            Alert: refund amount is higher than refundable deposit (₹{refundable.toFixed(2)}).
                          </div>
                          <div className="text-[11px] text-yellow-700 mt-0.5">
                            Add a reason in this box or in return notes below. If the amount exceeds the Online max, it will be
                            processed as Offline (manual) automatically.
                          </div>
                          <div className="text-[11px] text-yellow-700 mt-0.5">
                            Online refundable max: ₹{selectedMaxOnlineRefundable.toFixed(2)}
                          </div>
                          <div className="mt-2">
                            <label className="block text-xs font-semibold text-yellow-800 mb-1">
                              Refund reason *{" "}
                              <span className="font-normal text-yellow-700">(saved on refund row)</span>
                            </label>
                            <textarea
                              rows={2}
                              className="w-full border border-yellow-200 rounded-lg px-3 py-2 text-sm bg-white"
                              value={returnForm.refund_reason}
                              onChange={(e) => setReturnForm((p) => ({ ...p, refund_reason: e.target.value }))}
                              placeholder="Why refund is more than deposit?"
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : null}
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-sm font-semibold text-gray-800 mb-2">Refund history</div>
                {selectedRefunds.length === 0 ? (
                  <div className="text-xs text-gray-500">No refunds recorded yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="text-gray-500">
                        <tr>
                          <th className="text-left py-1 pr-3">Refund ID</th>
                          <th className="text-left py-1 pr-3">Amount</th>
                          <th className="text-left py-1 pr-3">Time</th>
                          <th className="text-left py-1 pr-3">Refund note</th>
                          <th className="text-left py-1 pr-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedRefunds.map((rf) => (
                          <tr key={rf.id || rf.refund_id}>
                            <td className="py-1 pr-3 font-medium text-gray-800">{rf.refund_id || "-"}</td>
                            <td className="py-1 pr-3 text-gray-700">₹{Number(rf.amount || 0).toFixed(2)}</td>
                            <td className="py-1 pr-3 text-gray-700">{formatListingDateTime(rf.refunded_at || rf.created_at)}</td>
                            <td className="py-1 pr-3 text-gray-600 max-w-[14rem] whitespace-pre-wrap break-words">
                              {rf.refund_reason ? String(rf.refund_reason) : rf.notes ? String(rf.notes) : "—"}
                            </td>
                            <td className="py-1 pr-3">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                {String(rf.status || "processed")}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Return notes <span className="font-normal text-gray-400">(saved on booking)</span>
                </label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={returnForm.return_supplier_note}
                  onChange={(e) => setReturnForm((p) => ({ ...p, return_supplier_note: e.target.value }))}
                  placeholder="Damage info, extra km settlement, vehicle condition, etc."
                />
              </div>
            </>
          )}
        </div>
      </Dialog>
    </AdminPageLayout>
  );
}

